import dayjs from 'dayjs';
import {action, computed, makeObservable, observable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {TextInput} from '../../../../../uikit/TextInput';
import type {PayloadAction} from '../../../../../utils/events';
import type {GetComponentProps} from '../../../../../utils/utility-types';
import type {CRUTaskConfig, ScheduledTask, TaskCoachAttachment} from '../types';

import {getSecondsFromMidnight} from '../../../../../utils/datetime';
import {resolveFileIds} from '../../../../../utils/entity/file';
import {isEditionEvergreen} from '../../../../../utils/entity/program';
import {eventPayloadActionCreator} from '../../../../../utils/events';
import {FlagManager} from '../../../../../utils/store';
import {createTextInputField} from '../../../../../utils/validation/createTextInputField';
import {CRUProgramScheduleEventStore} from './CRUProgramScheduleEvent';
import {REPEATS} from './utils';

export class CRUProgramScheduleTaskStore extends CRUProgramScheduleEventStore {
  addAttachmentModalFlag = new FlagManager();
  attachments: TaskCoachAttachment[] = [];
  stopDay: ScheduledTask['stop_day'] = null;
  private duration = 2 * 60 * 60;

  constructor(config: CRUTaskConfig) {
    super(config);

    const {task} = config || {};

    this.fields.stopDay = createTextInputField('stopDay', {
      customValidate: action(() => {
        const field = this.fields.stopDay;

        let isValid = true;

        field.error = null;

        if (this.stopDay !== null) {
          if (this.stopDay > this.programDuration - 1) {
            field.error = i18n.t(
              'shared:CRUProgramScheduleTask.stop_day_max_error_message',
              {
                value: this.programDuration,
              },
            );
            isValid = false;
          } else if (this.stopDay < this.day) {
            field.error = i18n.t(
              'shared:CRUProgramScheduleTask.stop_day_min_error_message',
              {
                value: this.day + 1,
              },
            );
            isValid = false;
          }
        }

        field.isValid = isValid;

        return isValid;
      }),
      validationRule: {
        type: 'string',
      },
    });

    if (task) {
      this.fields.title.value = task.title;
      this.fields.description.value = task.description;
      this.day = task.day || this.day;
      this.stopDay = task.stop_day;
      this.duration = task.duration || this.duration;
      this.date = dayjs(this.date)
        .startOf('day')
        .add(task.time, 'second')
        .toDate();
      this.groupUuid = task.uuid || this.groupUuid;
      this.uuid = task.edition_uuid || this.uuid;
      this.repeat =
        this.availableRepeats.find(
          repeat => task.repeat.days === repeat.interval,
        ) || this.repeat;
      this.initialRepeat = this.repeat;
      this.attachments = task.coach_attachments.filter(Boolean);
    }

    this.fields.day.value = `${this.day + 1}`;

    if (this.stopDay) {
      this.fields.stopDay.value = `${this.stopDay + 1}`;
    }

    this.isEdit = Boolean(task);

    makeObservable(this, {
      attachments: observable,
      getTask: action.bound,
      hasAttachments: computed,
      onAddAttachments: action.bound,
      onRemoveAttachment: action.bound,
      onStopDayChange: action.bound,
      setStopDay: action.bound,
      stopDay: observable,
      title: computed,
      toggleStopDay: action.bound,
    });
  }

  /**
   * Override this to calc available repeats depends on stop day too
   */
  override get availableRepeats() {
    const duration = this.stopDay
      ? this.stopDay - this.day + 1
      : this.programDuration;

    return REPEATS.filter(repeat => duration > repeat.interval);
  }

  get hasAttachments() {
    return this.attachments.length > 0;
  }

  override get recurringDisclaimerTitle() {
    return i18n.t('shared:CRUCalendarEvent.recurring_disclaimer.task.title');
  }

  override get shouldShowRecurringDisclaimer() {
    return isEditionEvergreen(this.lastEdition);
  }

  get title() {
    /**
     * @i18n_parser
     *
     * t('shared:CRUProgramScheduleTask.title.create')
     * t('shared:CRUProgramScheduleTask.title.update')
     */
    return i18n.t(
      `shared:CRUProgramScheduleTask.title.${this.isEdit ? 'update' : 'create'}`,
    );
  }

  async getTask() {
    if (this.isFormValid) {
      const attachmentIds = await resolveFileIds(this.attachments);

      const task: ScheduledTask = {
        coach_attachment_ids: attachmentIds,
        coach_attachments: this.attachments.slice(),
        day: this.day,
        description: this.fields.description.value,
        duration: this.duration,
        edition_uuid: this.uuid,
        repeat: {
          days: this.repeat.interval || null,
          type: this.repeat.interval ? 'interval' : 'once',
        },
        stop_day: this.stopDay,
        time: getSecondsFromMidnight(this.date),
        title: this.fields.title.value,
        uuid: this.groupUuid,
      };

      return task;
    }
  }

  onAddAttachments(actionObj: PayloadAction<TaskCoachAttachment[]>) {
    const files = actionObj.payload;

    this.addAttachmentModalFlag.setValue(false);

    if (files) {
      this.attachments.push(...files);
    }
  }

  onRemoveAttachment(actionObj: PayloadAction<TaskCoachAttachment>) {
    const file = actionObj.payload;

    const index = this.attachments.findIndex(
      attachment => attachment._id === file?._id,
    );

    if (index > -1) {
      this.attachments.splice(index, 1);
    }
  }

  onStopDayChange(
    e: Parameters<GetComponentProps<typeof TextInput>['onChange']>[0],
  ) {
    let stopDayText = e.payload?.text.replace(/\D/g, '') || '';

    if (stopDayText === '0') {
      stopDayText = '';
    }

    this.stopDay = stopDayText ? parseInt(stopDayText, 10) : null;

    this.setStopDay(this.stopDay, false);

    this.fields.stopDay.onChange?.(
      eventPayloadActionCreator({text: stopDayText}, e.event),
    );
  }

  setStopDay(day: ScheduledTask['stop_day'], updateFieldValue = true) {
    this.stopDay = day;

    if (this.stopDay) {
      this.stopDay -= 1;
    }

    if (updateFieldValue) {
      this.fields.stopDay.setValue(day ? `${day}` : '');
    }
  }

  toggleStopDay(value: boolean) {
    this.setStopDay(value ? this.programDuration : null);
  }
}
